import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { submissionsApi, Submission, FitLabel, GateStatus } from '../../lib/api';
// Phase 2 design system
import { AuroraPage, PageHeader, Card, Badge, Icon, Input, Select, EmptyState } from '../../components/ui';
import type { BadgeTone } from '../../components/ui';

const FIT_TONE: Record<FitLabel, BadgeTone> = {
  excellent: 'good', strong: 'good', moderate: 'warn', weak: 'danger', poor: 'danger',
};

const GATE_TONE: Record<GateStatus, BadgeTone> = {
  ok: 'good', pending: 'warn', missing: 'danger', unknown: 'neutral',
};

const selectStyle: React.CSSProperties = { width: 'auto', minWidth: 170, flex: '0 0 auto' };

// Compact table cells — thin rows so more submissions fit on screen.
const th: React.CSSProperties = {
  textAlign: 'left', fontSize: 11, fontWeight: 600, letterSpacing: 0.3,
  textTransform: 'uppercase', color: 'var(--t3)', padding: '8px 12px', whiteSpace: 'nowrap',
};
const thNum: React.CSSProperties = { ...th, textAlign: 'right' };
const td: React.CSSProperties = { padding: '8px 12px', fontSize: 13, color: 'var(--t2)', verticalAlign: 'middle' };
const tdNum: React.CSSProperties = { ...td, textAlign: 'right', fontWeight: 600, color: 'var(--t1)', fontVariantNumeric: 'tabular-nums' };

export default function SubmissionList() {
  const nav = useNavigate();
  const [subs, setSubs] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fitFilter, setFitFilter] = useState<string>('');
  const [gateFilter, setGateFilter] = useState<string>('');
  const [stageFilter, setStageFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  // Debounced copy of `search` — the list reloads off this, so typing a
  // name fires one request after you pause rather than one per keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const params: Record<string, string> = {};
      if (fitFilter) params.fit_label = fitFilter;
      if (gateFilter) params.gate_status = gateFilter;
      if (stageFilter) params.stage_key = stageFilter;
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      const res = await submissionsApi.list(params);
      setSubs(res.data.submissions);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load submissions');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [fitFilter, gateFilter, stageFilter, debouncedSearch]);

  return (
    <AuroraPage style={{ maxWidth: 1400, margin: '0 auto' }}>
      <PageHeader
        icon={<Icon name="document" size={22} />}
        title="Submissions"
        subtitle={loading ? 'Loading…' : `${subs.length} submission${subs.length === 1 ? '' : 's'}`}
      />

      <Card padded style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by candidate, email, or job…"
            icon={<Icon name="search" size={15} />}
            aria-label="Search submissions"
            style={{ minWidth: 260, flex: '1 1 260px' }}
          />
          <Select value={fitFilter} onChange={(e) => setFitFilter(e.target.value)} style={selectStyle} aria-label="Filter by fit label">
            <option value="">All fit labels</option>
            <option value="excellent">Excellent</option>
            <option value="strong">Strong</option>
            <option value="moderate">Moderate</option>
            <option value="weak">Weak</option>
            <option value="poor">Poor</option>
          </Select>
          <Select value={gateFilter} onChange={(e) => setGateFilter(e.target.value)} style={selectStyle} aria-label="Filter by gate status">
            <option value="">All gate statuses</option>
            <option value="ok">OK</option>
            <option value="pending">Pending</option>
            <option value="missing">Missing</option>
            <option value="unknown">Unknown</option>
          </Select>
          <Select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)} style={selectStyle} aria-label="Filter by stage">
            <option value="">All stages</option>
            <option value="internal_review">Internal Review</option>
            <option value="submitted">Submitted</option>
            <option value="client_submitted">Client Submitted</option>
            <option value="interview">Interview</option>
            <option value="offer">Offer</option>
            <option value="confirmed">Confirmed</option>
            <option value="placed">Placed</option>
            <option value="rejected">Rejected</option>
            <option value="withdrawn">Withdrawn</option>
          </Select>
        </div>
      </Card>

      {error && <div style={{ padding: 10, background: 'var(--danger-tint)', color: 'var(--danger-fg)', borderRadius: 'var(--r-md)', marginBottom: 12 }}>{error}</div>}

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--t3)' }}>Loading…</div>
      ) : subs.length === 0 ? (
        <EmptyState
          icon={<Icon name="document" size={26} />}
          title="No submissions yet"
          description="Create one from a job's matching-candidates list."
        />
      ) : (
        <Card padded={false} style={{ overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--surface-subtle)', borderBottom: '1px solid var(--border)' }}>
                  <th style={th}>Candidate</th>
                  <th style={th}>Job</th>
                  <th style={thNum}>Score</th>
                  <th style={th}>Fit</th>
                  <th style={th}>Gate</th>
                  <th style={th}>Stage</th>
                </tr>
              </thead>
              <tbody>
                {subs.map((s) => (
                  <tr
                    key={s.id}
                    onClick={() => nav(`/submissions/${s.id}`)}
                    style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-subtle)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <td style={{ ...td, maxWidth: 260 }}>
                      <div style={{ fontWeight: 600, color: 'var(--t1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {s.candidate_name ?? s.candidate_id.slice(0, 8)}
                      </div>
                      {s.candidate_role && <div style={{ fontSize: 11, color: 'var(--t3)' }}>{s.candidate_role}</div>}
                    </td>
                    <td style={{ ...td, maxWidth: 300 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Link to={`/jobs/${s.job_id}`} onClick={(e) => e.stopPropagation()} style={{ color: 'var(--pr)', textDecoration: 'none' }}>
                          {s.job_title ?? 'Job'}
                        </Link>
                      </div>
                      {(s.job_code || s.client_name) && (
                        <div style={{ fontSize: 11, color: 'var(--t3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {[s.job_code, s.client_name].filter(Boolean).join(' · ')}
                        </div>
                      )}
                    </td>
                    <td style={tdNum}>{s.ai_score != null ? s.ai_score : <span style={{ color: 'var(--t3)', fontWeight: 400 }}>—</span>}</td>
                    <td style={td}>
                      {s.ai_fit_label
                        ? <Badge tone={FIT_TONE[s.ai_fit_label]} style={{ textTransform: 'uppercase' }}>{s.ai_fit_label}</Badge>
                        : <span style={{ color: 'var(--t3)' }}>—</span>}
                    </td>
                    <td style={td}>
                      {s.gate_status && s.gate_status !== 'unknown'
                        ? <Badge tone={GATE_TONE[s.gate_status]} style={{ textTransform: 'uppercase' }}>{s.gate_status}</Badge>
                        : <span style={{ color: 'var(--t3)' }}>—</span>}
                    </td>
                    <td style={td}><StageChip color={s.stage_color ?? 'var(--t3)'} label={s.stage_label ?? s.stage_key ?? '—'} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </AuroraPage>
  );
}

// Stage colors are supplied by the backend (per-stage), so this chip keeps the
// DS Badge shape while honoring that dynamic color via a token-consistent tint.
function StageChip({ color, label }: { color: string; label: string }) {
  return (
    <Badge
      tone="neutral"
      style={{
        background: `color-mix(in srgb, ${color} 15%, transparent)`,
        color,
        borderColor: `color-mix(in srgb, ${color} 32%, transparent)`,
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Badge>
  );
}
